import React, { useEffect, useRef } from 'react';
import { useLocation } from 'react-router';
import SideNav from '../SideNav';
import classes from './index.module.scss';

/**
 * Props interface for the PageLayout component
 */
interface Props {
	/** Content to render in the main area */
	children: React.ReactNode;
	/** Whether to remove default padding from the main area */
	noPadding?: boolean;
}

/**
 * PageLayout component
 * Renders the main application layout with side navigation and content area
 * Handles scrolling to top when route changes
 *
 * @param {Props} props - Component props
 * @returns {JSX.Element} - Rendered component
 */
const PageLayout = ({ noPadding, children }: Props) => {
	// Reference to the main content area for scrolling control
	const mainAreaRef = useRef<HTMLDivElement>(null);
	// Current location from router
	const pathName = useLocation();

	/**
	 * Scroll to top when route changes
	 * Ensures content always starts at the top when navigating
	 */
	useEffect(() => {
		if (mainAreaRef.current) {
			mainAreaRef.current.scrollTo(0, 0);
		}
	}, [pathName]);

	return (
		<div className={classes['layout']}>
			{/* Side navigation component */}
			<SideNav />
			{/* Main content area */}
			<div
				className={classes['mainArea']}
				style={{
					// Remove padding if noPadding is true
					padding: noPadding ? 0 : undefined,
				}}
				ref={mainAreaRef}
			>
				{children}
			</div>
		</div>
	);
};

export default PageLayout;
